"use client";

import { Callout, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import * as React from "react";
import { useResendUserInvite } from "./api/user.js";
import { AlertDialog, Button, Dialog } from "./elements.js";
import { Member } from "../api/endpoint.js";
import { Translation } from "./i18n/translation.js";

interface ResendInviteDialogProps extends AlertDialog.RootProps {
  user: Member;
  children?: React.ReactNode;
}

export function ResendInviteDialog({
  children,
  user,
  ...props
}: ResendInviteDialogProps) {
  const resendInvite = useResendUserInvite();
  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);
  const successButtonRef = React.useRef<HTMLButtonElement>(null);
  const [successDialogIsOpen, setSuccessDialogIsOpen] = React.useState(false);

  const onSubmitForm = () => {
    resendInvite.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          setSuccessDialogIsOpen(true);
        },
      },
    );
  };

  return (
    <>
      <AlertDialog.Root {...props}>
        {children && <AlertDialog.Trigger>{children}</AlertDialog.Trigger>}

        <AlertDialog.Content
          maxWidth="480px"
          onOpenAutoFocus={() => {
            requestAnimationFrame(() => {
              cancelButtonRef.current?.focus();
            });
          }}
        >
          <AlertDialog.Title>
            <Translation
              defaultMessage="Resend invite?"
              id="oSINc8"
              description="Dialog title asking to confirm resending an invitation"
            />
          </AlertDialog.Title>
          <Flex mb="4" direction="column" gap="3">
            <AlertDialog.Description>
              <Translation
                defaultMessage="Are you sure you want to resend the invite to {email}?"
                id="46jEJT"
                description="Confirmation message asking if user wants to resend invite to specific email"
                values={{ email: <Text weight="bold">{user.email}</Text> }}
              />
            </AlertDialog.Description>
          </Flex>

          {resendInvite.error ? (
            <Callout.Root color="red" mt="4" mb="-2">
              <Callout.Text>
                {getMutationErrorMessage(resendInvite.error)}
              </Callout.Text>
            </Callout.Root>
          ) : null}

          <Flex gap="3" justify="end" mt="5" asChild>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                onSubmitForm();
              }}
            >
              <AlertDialog.Cancel>
                <Button
                  type="button"
                  variant="secondary"
                  ref={cancelButtonRef}
                  disabled={resendInvite.isPending}
                >
                  <Translation
                    defaultMessage="Cancel"
                    id="Ecig7v"
                    description="Button to cancel resending invite"
                  />
                </Button>
              </AlertDialog.Cancel>
              <Button
                variant="destructive"
                type="submit"
                loading={resendInvite.isPending}
              >
                <Translation
                  defaultMessage="Resend"
                  id="t73qag"
                  description="Button to confirm resending the invitation"
                />
              </Button>
            </form>
          </Flex>
        </AlertDialog.Content>

        {/* mirror errors in a live region */}
        <VisuallyHidden asChild>
          <section aria-live="polite">
            {getMutationErrorMessage(resendInvite.error)}
          </section>
        </VisuallyHidden>
      </AlertDialog.Root>
      <Dialog.Root
        open={successDialogIsOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            props.onOpenChange?.(false);
          }
          setSuccessDialogIsOpen(isOpen);
        }}
      >
        <Dialog.Content
          maxWidth="360px"
          onOpenAutoFocus={() => {
            requestAnimationFrame(() => {
              successButtonRef.current?.focus();
            });
          }}
        >
          <Dialog.Title>
            <Translation
              defaultMessage="Invite sent"
              id="5uMNqG"
              description="Dialog title confirming invite was sent"
            />
          </Dialog.Title>
          <Dialog.Description>
            <Translation
              defaultMessage="The invite email has been resent to {email}"
              id="fb2S3+"
              description="Message confirming invite was resent to specific email"
              values={{ email: <Text weight="bold">{user.email}</Text> }}
            />
          </Dialog.Description>
          <Flex gap="3" justify="end" mt="5">
            <Dialog.Close>
              <Button ref={successButtonRef}>
                <Translation
                  defaultMessage="Close"
                  id="Mixw+w"
                  description="Button to close the success dialog"
                />
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}

function getMutationErrorMessage(error: unknown) {
  if (!error) {
    return "";
  }
  // TODO Handle server errors
  return (
    <Translation
      defaultMessage="There was an error sending the invite. Please try again."
      id="090qDK"
      description="Error message shown when resending invite fails"
    />
  );
}
